import * as PropTypes from 'prop-types';
import * as React from 'react';
import SigPad, {
  type Options,
  type PointGroup,
  type ToSVGOptions,
} from 'signature_pad';
import { debounce } from 'throttle-debounce';

type Props = {
  width?: number;
  height?: number;
  options?: Options;
  canvasProps?: { [key: string]: string | { [key: string]: string } };
  onAutoSave?: (dataUrl: string, data: PointGroup[]) => void;
  autoSaveDelay?: number;
  compressionOptions?: {
    format?: 'image/png' | 'image/jpeg' | 'image/webp';
    quality?: number;
  };
} & DefaultProps;

type DefaultProps = {
  redrawOnResize: boolean;
  debounceInterval: number;
};

type State = {
  canvasWidth: number;
  canvasHeight: number;
};

/**
 * @class
 * @classdesc Signature pad component.
 * @extends {PureComponent}
 */
class SignaturePad extends React.PureComponent<Props, State> {
  static displayName = 'react-signature-pad-wrapper';

  static propTypes = {
    width: PropTypes.number,
    height: PropTypes.number,
    options: PropTypes.object,
    canvasProps: PropTypes.object,
    redrawOnResize: PropTypes.bool.isRequired,
    debounceInterval: PropTypes.number.isRequired,
    onAutoSave: PropTypes.func,
    autoSaveDelay: PropTypes.number,
    compressionOptions: PropTypes.object,
  };

  static defaultProps: DefaultProps = {
    redrawOnResize: false,
    debounceInterval: 150,
  };

  private canvasRef = React.createRef<HTMLCanvasElement>();

  private signaturePad!: SigPad;

  private callResizeHandler!: debounce<() => void>;

  private autoSaveHandler!: debounce<() => void>;

  private touchEventHandlers?: {
    preventDefault: (e: Event) => void;
    canvas: HTMLCanvasElement;
  };

  /**
   * Create a new signature pad.
   *
   * @param {Props} props
   */
  constructor(props: Props) {
    super(props);

    this.state = { canvasWidth: 0, canvasHeight: 0 };

    this.callResizeHandler = debounce<() => void>(
      this.props.debounceInterval,
      this.handleResize.bind(this),
    );

    // Auto-save handler that triggers when user stops drawing
    this.autoSaveHandler = debounce<() => void>(
      this.props.autoSaveDelay || 1000,
      this.handleAutoSave.bind(this),
    );
  }

  /**
   * Initialise the signature pad once the canvas element is rendered.
   *
   * @return {void}
   */
  componentDidMount(): void {
    const canvas = this.canvasRef.current;

    if (canvas) {
      if (!this.props.width || !this.props.height) {
        canvas.style.width = '100%';
        window.addEventListener('resize', this.callResizeHandler);
      }

      // Scale the canvas first before creating the signature pad
      this.scaleCanvas(canvas, true);

      // Create the signature pad after scaling is complete
      this.signaturePad = new SigPad(canvas, this.props.options);

      // Add event listeners for auto-save functionality
      this.signaturePad.addEventListener(
        'endStroke',
        this.handleStrokeEnd.bind(this),
      );

      // Add mobile touch event handling
      this.addMobileTouchHandling(canvas);
    }
  }

  /**
   * Remove the resize event listener and switch the signature pad off on
   * unmount.
   *
   * @return {void}
   */
  componentWillUnmount(): void {
    if (!this.props.width || !this.props.height) {
      window.removeEventListener('resize', this.callResizeHandler);
    }

    // Clean up touch event handlers
    if (this.touchEventHandlers) {
      const { preventDefault, canvas } = this.touchEventHandlers;
      canvas.removeEventListener('touchstart', preventDefault);
      canvas.removeEventListener('touchmove', preventDefault);
      canvas.removeEventListener('touchend', preventDefault);
      canvas.removeEventListener('touchcancel', preventDefault);
      canvas.removeEventListener('contextmenu', preventDefault);
    }

    this.signaturePad.off();
  }

  /**
   * Add mobile touch event handling to prevent coordinate offset issues
   *
   * @param {HTMLCanvasElement} canvas
   * @return {void}
   */
  addMobileTouchHandling(canvas: HTMLCanvasElement): void {
    // Prevent default touch behaviors that can interfere with drawing
    const preventDefault = (e: Event) => {
      e.preventDefault();
    };

    // Add touch event listeners to prevent scrolling and other touch gestures
    canvas.addEventListener('touchstart', preventDefault, { passive: false });
    canvas.addEventListener('touchmove', preventDefault, { passive: false });
    canvas.addEventListener('touchend', preventDefault, { passive: false });
    canvas.addEventListener('touchcancel', preventDefault, { passive: false });

    // Prevent context menu on long press
    canvas.addEventListener('contextmenu', preventDefault);

    // Store references for cleanup
    this.touchEventHandlers = {
      preventDefault,
      canvas,
    };
  }

  /**
   * Handle stroke end event to trigger auto-save
   *
   * @return {void}
   */
  handleStrokeEnd(): void {
    if (this.props.onAutoSave && !this.signaturePad.isEmpty()) {
      this.autoSaveHandler();
    }
  }

  /**
   * Handle auto-save by calling the onAutoSave callback
   *
   * @return {void}
   */
  handleAutoSave(): void {
    if (this.props.onAutoSave && !this.signaturePad.isEmpty()) {
      const { compressionOptions } = this.props;
      const format = compressionOptions?.format || 'image/jpeg';
      const quality = compressionOptions?.quality || 0.7;

      const dataUrl = this.signaturePad.toDataURL(format, quality);
      const data = this.signaturePad.toData();
      this.props.onAutoSave(dataUrl, data);
    }
  }

  /**
   * Manually trigger auto-save
   *
   * @return {void}
   */
  triggerAutoSave(): void {
    this.handleAutoSave();
  }

  /**
   * Update auto-save delay when props change
   *
   * @param {Props} prevProps
   * @return {void}
   */
  componentDidUpdate(prevProps: Props): void {
    if (prevProps.autoSaveDelay !== this.props.autoSaveDelay) {
      this.autoSaveHandler = debounce<() => void>(
        this.props.autoSaveDelay || 1000,
        this.handleAutoSave.bind(this),
      );
    }
  }

  /**
   * Get the original signature_pad instance.
   *
   * @return {SigPad}
   */
  get instance(): SigPad {
    return this.signaturePad;
  }

  /**
   * Get the canvas ref.
   *
   * @return {Object}
   */
  get canvas(): React.RefObject<HTMLCanvasElement | null> {
    return this.canvasRef;
  }

  /**
   * Set the radius of a single dot.
   *
   * @param {number} dotSize
   * @return {void}
   */
  set dotSize(dotSize: number) {
    this.signaturePad.dotSize = dotSize;
  }

  /**
   * Get the radius of a single dot.
   *
   * @return {number}
   */
  get dotSize(): number {
    return this.signaturePad.dotSize;
  }

  /**
   * Set the minimum width of a line.
   *
   * @param {number} minWidth
   * @return {void}
   */
  set minWidth(minWidth: number) {
    this.signaturePad.minWidth = minWidth;
  }

  /**
   * Get the minimum width of a line.
   *
   * @return {number}
   */
  get minWidth(): number {
    return this.signaturePad.minWidth;
  }

  /**
   * Get the maximum width of a line.
   *
   * @param {number} maxWidth
   * @return {void}
   */
  set maxWidth(maxWidth: number) {
    this.signaturePad.maxWidth = maxWidth;
  }

  /**
   * Get the maximum width of a line.
   *
   * @return {number}
   */
  get maxWidth(): number {
    return this.signaturePad.maxWidth;
  }

  /**
   * Set the throttle for drawing the next point at most once every x ms.
   *
   * @param {number} throttle
   * @return {void}
   */
  set throttle(throttle: number) {
    this.signaturePad.throttle = throttle;
  }

  /**
   * Get the throttle for drawing the next point at most once every x ms.
   *
   * @return {number}
   */
  get throttle(): number {
    return this.signaturePad.throttle;
  }

  /**
   * Set the color used to clear the background.
   *
   * @param {string} color
   * @return {void}
   */
  set backgroundColor(color: string) {
    this.signaturePad.backgroundColor = color;
  }

  /**
   * Get the color used to clear the background.
   *
   * @return {string}
   */
  get backgroundColor(): string {
    return this.signaturePad.backgroundColor;
  }

  /**
   * Set the color used to draw the lines.
   *
   * @param {string} color
   * @return {void}
   */
  set penColor(color: string) {
    this.signaturePad.penColor = color;
  }

  /**
   * Get the color used to draw the lines.
   *
   * @return {string}
   */
  get penColor(): string {
    return this.signaturePad.penColor;
  }

  /**
   * Set weight used to modify new velocity based on the previous velocity.
   *
   * @param {number} weight
   * @return {void}
   */
  set velocityFilterWeight(weight: number) {
    this.signaturePad.velocityFilterWeight = weight;
  }

  /**
   * Get weight used to modify new velocity based on the previous velocity.
   *
   * @return {number}
   */
  get velocityFilterWeight(): number {
    return this.signaturePad.velocityFilterWeight;
  }

  /**
   * Determine if the canvas is empty.
   *
   * @return {boolean}
   */
  isEmpty(): boolean {
    return this.signaturePad.isEmpty();
  }

  /**
   * Clear the canvas.
   *
   * @return {void}
   */
  clear(): void {
    this.signaturePad.clear();
  }

  /**
   * Draw a signature from a data URL.
   *
   * @param {string} dataUrl
   * @param {object} options
   * @return {void}
   */
  fromDataURL(
    dataUrl: string,
    options: Partial<{
      ratio: number;
      width: number;
      height: number;
      xOffset: number;
      yOffset: number;
    }> = {},
  ): void {
    this.signaturePad.fromDataURL(dataUrl, options);
  }

  /**
   * Get the signature data as a compressed data URL for PDF generation.
   *
   * @param {?string} format
   * @param {?number} quality
   * @return {string}
   */
  getCompressedDataURL(
    format: string = 'image/jpeg',
    quality: number = 0.6,
  ): string {
    return this.signaturePad.toDataURL(format, quality);
  }

  /**
   * Get the signature data as a data URL.
   *
   * @param {?string} mime
   * @param {?number} encoderOptions
   * @return {string}
   */
  toDataURL(type?: string, encoderOptions?: number): string {
    return this.signaturePad.toDataURL(type, encoderOptions);
  }

  /**
   * Get the signature data as an SVG string without converting to base64.
   *
   * @param {?ToSVGOptions} svgOptions
   * @return {string}
   */
  toSVG(svgOptions?: ToSVGOptions): string {
    return this.signaturePad.toSVG(svgOptions);
  }

  /**
   * Draw a signature from an array of point groups.
   *
   * @param {PointGroup[]} data
   * @return {void}
   */
  fromData(data: PointGroup[]): void {
    this.signaturePad.fromData(data);
  }

  /**
   * Get the signature pad data an array of point groups.
   *
   * @return {PointGroup[]}
   */
  toData(): PointGroup[] {
    return this.signaturePad.toData();
  }

  /**
   * Turn the signature pad off.
   *
   * @return {void}
   */
  off(): void {
    this.signaturePad.off();
  }

  /**
   * Turn the signature pad on.
   *
   * @return {void}
   */
  on(): void {
    this.signaturePad.on();
  }

  /**
   * Handle a resize event.
   *
   * @return {void}
   */
  handleResize(): void {
    const canvas = this.canvasRef.current;

    if (canvas) {
      this.scaleCanvas(canvas);
    }
  }

  /**
   * Scale the canvas.
   *
   * @param {HTMLCanvasElement} canvas
   * @param {boolean} skipSignaturePadRecreation - Skip recreating the signature pad
   * @return {void}
   */
  scaleCanvas(
    canvas: HTMLCanvasElement,
    skipSignaturePadRecreation: boolean = false,
  ): void {
    // Get the actual display dimensions
    const displayWidth = this.props.width || canvas.offsetWidth;
    const displayHeight = this.props.height || canvas.offsetHeight;

    // Use device pixel ratio for crisp rendering on high-DPI displays
    const ratio = window.devicePixelRatio || 1;

    // Calculate actual canvas dimensions
    const width = displayWidth * ratio;
    const height = displayHeight * ratio;

    // Avoid needlessly setting height / width if dimensions haven't changed
    const { canvasWidth, canvasHeight } = this.state;

    if (width === canvasWidth && height === canvasHeight) return;

    let data;

    if (
      this.props.redrawOnResize &&
      this.signaturePad &&
      !this.signaturePad.isEmpty()
    ) {
      data = this.signaturePad.toDataURL();
    }

    // Set the actual canvas dimensions for high-DPI rendering
    canvas.width = width;
    canvas.height = height;

    // Set the display size (CSS pixels) - this is crucial for proper touch coordinate mapping
    canvas.style.width = `${displayWidth}px`;
    canvas.style.height = `${displayHeight}px`;

    this.setState({ canvasWidth: width, canvasHeight: height });

    const ctx = canvas.getContext('2d');

    if (ctx) {
      // Scale the drawing context to match the device pixel ratio
      ctx.scale(ratio, ratio);

      // Clear the canvas first
      ctx.clearRect(0, 0, displayWidth, displayHeight);
    }

    // Only recreate the signature pad if not skipping recreation
    if (!skipSignaturePadRecreation) {
      // Recreate the signature pad to ensure proper coordinate handling
      if (this.signaturePad) {
        this.signaturePad.off();
      }

      // Create signature pad with proper options for mobile touch handling
      const options = {
        ...this.props.options,
        // Ensure proper touch handling on mobile devices
        touchAction: 'none',
        // Disable mouse events on touch devices to prevent conflicts
        preventDefault: true,
      };

      this.signaturePad = new SigPad(canvas, options);

      // Add event listeners for auto-save functionality
      this.signaturePad.addEventListener(
        'endStroke',
        this.handleStrokeEnd.bind(this),
      );

      // Add mobile touch handling for the recreated signature pad
      this.addMobileTouchHandling(canvas);

      if (data) {
        this.signaturePad.fromDataURL(data);
      } else if (this.signaturePad) {
        this.signaturePad.clear();
      }
    }
  }

  /**
   * Render the signature pad component.
   *
   * @return {ReactNode}
   */
  render(): React.ReactNode {
    const { canvasProps } = this.props;

    return (
      <canvas
        data-testid="canvas-element"
        ref={this.canvasRef}
        {...canvasProps}
      />
    );
  }
}

export default SignaturePad;
